import { useState, useEffect } from "react";
import { Car, Home, HeartPulse, Umbrella, ShieldCheck, Check, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useContactModal } from "@/components/ContactModalProvider";
import { contactFieldModes, contactFieldsError, contactPlaceholder, contactPayload } from "@/lib/contactFields";

// Icône indicative par mot-clé de l'option (garde un rendu correct même si
// l'admin personnalise `quote_options` avec d'autres libellés que ceux du
// thème "Assurance" — repli sur ShieldCheck, jamais d'icône manquante).
function iconFor(label) {
  const l = label.toLowerCase();
  if (l.includes("auto") || l.includes("voiture")) return Car;
  if (l.includes("habitat") || l.includes("maison") || l.includes("logement")) return Home;
  if (l.includes("sant")) return HeartPulse;
  if (l.includes("prévoy") || l.includes("prevoy") || l.includes("emprunt")) return Umbrella;
  return ShieldCheck;
}

const STEP_LABELS = ["Garantie", "Coordonnées", "Besoin", "Récapitulatif"];
const FIELD_CLASS = "w-full rounded-btn border-control border-stone bg-input-fill px-4 py-3 text-[14px] text-ink outline-none focus:border-gold focus:bg-white transition-colors";

// Variante "étapes" du formulaire de contact — même endpoint /contact-request
// que le formulaire simple (ContactSection.jsx), juste une présentation en
// plusieurs écrans plutôt qu'un seul long formulaire. N'affecte que les
// sections "contact" dont form_style="stepper" (voir DEFAULT_PROPS côté
// backend) : le formulaire simple d'origine reste inchangé partout ailleurs.
// `onSent` (optionnel) : appelé juste après un envoi réussi. La fenêtre de
// contact s'en sert pour se refermer AVANT la confirmation partagée —
// sans quoi deux fenêtres se superposeraient. En usage autonome (colonne
// de la section Contact), rien n'est passé et rien ne change.
// `fieldSettings` : props de la section (field_name / field_email /
// field_phone, Studio de thème) — chaque coordonnée est obligatoire,
// facultative ou masquée (voir lib/contactFields.js).
export default function ContactQuoteStepper({ quoteOptions, onSent, fieldSettings }) {
  const modes = contactFieldModes(fieldSettings);
  const options = (quoteOptions || "").split(",").map((s) => s.trim()).filter(Boolean);
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const { notifySent } = useContactModal();
  const [form, setForm] = useState({ option: options[0] || "", name: "", email: "", phone: "", postal_code: "", message: "" });

  // Sync la sélection initiale quand quoteOptions change (ex: édition live
  // dans le canvas du Studio de thème). Ne réinitialise que si l'option
  // actuellement choisie n'existe plus dans la nouvelle liste.
  useEffect(() => {
    const newOpts = (quoteOptions || "").split(",").map((s) => s.trim()).filter(Boolean);
    setForm((f) => (newOpts.includes(f.option) ? f : { ...f, option: newOpts[0] || "" }));
  }, [quoteOptions]);

  const [touched, setTouched] = useState(false);
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Les coordonnées bloquent l'étape 2 tant qu'elles ne sont pas valides ;
  // le message n'apparaît qu'après une première tentative de continuer.
  const contactError = contactFieldsError(form, modes);
  const canAdvance =
    (step === 0 && !!form.option) ||
    (step === 1 && !contactError) ||
    step === 2 ||
    step === 3;

  const next = () => {
    if (step === 1) setTouched(true);
    if (!canAdvance) return;
    setStep((s) => Math.min(s + 1, STEP_LABELS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const lines = [`Garantie souhaitée : ${form.option}`];
      if (form.postal_code) lines.push(`Code postal : ${form.postal_code}`);
      lines.push("", form.message || "(aucun détail complémentaire)");
      // Le téléphone saisi n'était jamais transmis : il l'est désormais,
      // comme le nom et l'e-mail, et rien n'est envoyé pour un champ masqué.
      await api.post("/contact-request", {
        ...contactPayload(form, modes),
        message: lines.join("\n"),
        source: "home",
      });
      setDone(true);
      // L'écran de confirmation en place ci-dessous demeure après la
      // fermeture de la fenêtre : il tient lieu d'état final du formulaire
      // (et empêche un second envoi), la fenêtre ne fait que garantir que
      // la confirmation est vue.
      onSent?.();
      notifySent("Votre demande de devis est bien enregistrée. Un conseiller revient vers vous dans les plus brefs délais.", { name: contactPayload(form, modes).name });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'envoi, réessayez.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-stepper-success" className="rounded-card border-control border-stone bg-white p-8 flex flex-col items-center text-center gap-3">
        <span className="h-12 w-12 rounded-full bg-gold/15 flex items-center justify-center">
          <Check className="h-6 w-6 text-gold" />
        </span>
        <h3 className="font-display font-semibold text-[20px] text-ink">Demande envoyée</h3>
        <p className="text-[14px] text-graphite max-w-sm">
          Merci {contactPayload(form, modes).name}, un conseiller revient vers vous sous 24h pour votre devis {form.option ? `« ${form.option} »` : ""}.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="quote-stepper" onSubmit={step === 3 ? submit : (e) => { e.preventDefault(); next(); }} className="rounded-card border-control border-stone bg-white p-6 md:p-8">
      {/* Repères d'étape */}
      <div className="flex items-center mb-7" data-testid="quote-stepper-progress">
        {STEP_LABELS.map((label, i) => (
          <div key={label} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <span
                className={`h-8 w-8 rounded-full flex items-center justify-center text-[13px] font-semibold shrink-0 transition-colors border-2 ${
                  i < step ? "bg-gold border-gold text-white" : i === step ? "border-gold text-gold" : "border-stone text-ash"
                }`}
              >
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              <span className={`text-[11px] whitespace-nowrap ${i === step ? "text-ink font-medium" : "text-ash"}`}>{label}</span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <span className={`h-[2px] flex-1 mx-2 mb-4 rounded-full transition-colors ${i < step ? "bg-gold" : "bg-stone"}`} />
            )}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="space-y-3" data-testid="quote-step-option">
          <p className="text-[14px] font-medium text-ink">Quelle garantie vous intéresse ?</p>
          <div className="grid grid-cols-2 gap-3">
            {options.map((opt) => {
              const Icon = iconFor(opt);
              const active = form.option === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  data-testid={`quote-option-${opt}`}
                  onClick={() => setForm((f) => ({ ...f, option: opt }))}
                  className={`flex flex-col items-start gap-2.5 rounded-card border p-4 text-left transition-colors ${
                    active ? "border-gold bg-sand" : "border-stone hover:border-ash"
                  }`}
                >
                  <Icon className={`h-5 w-5 ${active ? "text-gold" : "text-ash"}`} />
                  <span className="text-[14px] font-medium text-ink">{opt}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4" data-testid="quote-step-contact">
          <p className="text-[14px] font-medium text-ink">Vos coordonnées</p>
          {modes.name !== "hidden" && (
            <input required={modes.name === "required"} aria-required={modes.name === "required"} autoComplete="name" placeholder={contactPlaceholder("Nom", modes.name)} data-testid="quote-name" value={form.name} onChange={setField("name")} className={FIELD_CLASS} />
          )}
          {modes.email !== "hidden" && (
            <input required={modes.email === "required"} aria-required={modes.email === "required"} type="email" autoComplete="email" placeholder={contactPlaceholder("Email", modes.email)} data-testid="quote-email" value={form.email} onChange={setField("email")} className={FIELD_CLASS} />
          )}
          {modes.phone !== "hidden" && (
            <input required={modes.phone === "required"} aria-required={modes.phone === "required"} type="tel" autoComplete="tel" placeholder={contactPlaceholder("Téléphone", modes.phone)} data-testid="quote-phone" value={form.phone} onChange={setField("phone")} className={FIELD_CLASS} />
          )}
          {modes.email === "optional" && modes.phone === "optional" && (
            <p className="text-[12px] text-graphite">Indiquez au moins un e-mail ou un numéro de téléphone pour être recontacté.</p>
          )}
          {touched && contactError && (
            <p role="alert" data-testid="quote-contact-error" className="text-[13px] text-red-600">{contactError}</p>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4" data-testid="quote-step-need">
          <p className="text-[14px] font-medium text-ink">Votre besoin</p>
          <input placeholder="Code postal (optionnel)" data-testid="quote-postal" value={form.postal_code} onChange={setField("postal_code")} className="w-full rounded-btn border-control border-stone bg-input-fill px-4 py-3 text-[14px] text-ink outline-none focus:border-gold focus:bg-white transition-colors" />
          <textarea rows={4} placeholder="Décrivez votre situation ou vos attentes…" data-testid="quote-message" value={form.message} onChange={setField("message")} className="w-full rounded-btn border-control border-stone bg-input-fill px-4 py-3 text-[14px] text-ink outline-none focus:border-gold focus:bg-white transition-colors resize-none" />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3" data-testid="quote-step-summary">
          <p className="text-[14px] font-medium text-ink">Récapitulatif</p>
          <dl className="rounded-card bg-sand p-4 space-y-2 text-[13px]">
            <div className="flex justify-between gap-3"><dt className="text-graphite">Garantie</dt><dd className="text-ink font-medium text-right">{form.option || "—"}</dd></div>
            {modes.name !== "hidden" && <div className="flex justify-between gap-3"><dt className="text-graphite">Nom</dt><dd className="text-ink font-medium text-right">{form.name || "—"}</dd></div>}
            {modes.email !== "hidden" && <div className="flex justify-between gap-3"><dt className="text-graphite">Email</dt><dd className="text-ink font-medium text-right break-all">{form.email || "—"}</dd></div>}
            {modes.phone !== "hidden" && form.phone && <div className="flex justify-between gap-3"><dt className="text-graphite">Téléphone</dt><dd className="text-ink font-medium text-right">{form.phone}</dd></div>}
            {form.postal_code && <div className="flex justify-between gap-3"><dt className="text-graphite">Code postal</dt><dd className="text-ink font-medium text-right">{form.postal_code}</dd></div>}
          </dl>
        </div>
      )}

      <div className="flex items-center gap-3 mt-7">
        {step > 0 && (
          <button type="button" onClick={back} data-testid="quote-back" className="inline-flex items-center gap-1.5 px-4 py-3.5 rounded-btn text-[14px] font-semibold text-ink border-control border-stone hover:bg-sand transition-colors">
            <ChevronLeft className="h-4 w-4" /> Retour
          </button>
        )}
        {step < 3 ? (
          <button type="submit" disabled={step !== 1 && !canAdvance} data-testid="quote-next" className="flex-1 inline-flex items-center justify-center gap-1.5 bg-ink text-white py-3.5 rounded-btn text-[14px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
            Continuer <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button type="submit" disabled={sending} data-testid="quote-submit" className="flex-1 inline-flex items-center justify-center gap-1.5 bg-ink text-white py-3.5 rounded-btn text-[14px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {sending ? "Envoi en cours…" : "Envoyer ma demande de devis"}
          </button>
        )}
      </div>
    </form>
  );
}
